import React, { useState } from "react";
import { Sidebar } from "../components/Sidebar";
import {
  ArrowLeft,
  AlertTriangle,
  Download,
  Share2,
  RefreshCw,
  AlertCircle,
  Menu,
  MessageSquare,
  Bot,
  User as UserIcon,
} from "lucide-react";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { useParams } from "@tanstack/react-router";
import { cn } from "../lib/utils";
import { visualizationData } from "./ai-agent/data/data";

// Test interface based on the actual data structure
interface Test {
  input: string;
  expected: string;
  received: string;
  success: boolean;
  id: string;
}

// Node interface for the hierarchical structure
interface TestNode {
  id: string;
  name: string;
  testCoverage?: number;
  children?: TestNode[];
  tests?: Test[];
}

// Conversation Message Interface
interface ConversationMessage {
  id: string;
  sender: "user" | "agent" | "system";
  content: string;
  timestamp: Date;
  metadata?: {
    responseTime?: number;
    tokensUsed?: number;
    confidence?: number;
    model?: string;
  };
}

// Utility function to recursively find a test by ID
const findTestById = (node: TestNode, testId: string): Test | null => {
  // Check if this node has tests
  if (node.tests) {
    const test = node.tests.find((t) => t.id === testId);
    if (test) return test;
  }

  // Recursively search children
  if (node.children) {
    for (const child of node.children) {
      const result = findTestById(child, testId);
      if (result) return result;
    }
  }

  return null;
};

// Helper function for test status colors
const getTestStatusColor = (status: string) => {
  switch (status) {
    case "passed":
      return "bg-green-900/30 text-green-400 border-green-600/30";
    case "failed":
      return "bg-red-900/30 text-red-400 border-red-600/30";
    case "running":
      return "bg-blue-900/30 text-blue-400 border-blue-600/30";
    case "pending":
      return "bg-gray-900/30 text-gray-400 border-gray-600/30";
    default:
      return "bg-gray-900/30 text-gray-400 border-gray-600/30";
  }
};

const TestDetailsPage: React.FC = () => {
  const { testRunId, testId } = useParams({
    from: "/test-runs/$testRunId/test/$testId",
  });
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // Find the actual test data
  const actualTest = findTestById(visualizationData, testId);

  // If test not found, show error state
  if (!actualTest) {
    return (
      <div className="flex h-screen bg-gray-900">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <AlertTriangle className="h-12 w-12 text-red-400 mx-auto mb-4" />
            <h1 className="text-xl font-bold text-gray-100 mb-2">
              Test Not Found
            </h1>
            <p className="text-gray-400">
              The test with ID "{testId}" could not be found.
            </p>
            <Button
              variant="ghost"
              onClick={() => window.history.back()}
              className="inline-flex items-center mt-4 text-blue-400 hover:text-blue-300"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Test Run
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Create conversation messages from the actual test data
  const [messages, setMessages] = useState<ConversationMessage[]>([
    {
      id: "1",
      sender: "system",
      content: `Starting test: ${actualTest.id}`,
      timestamp: new Date(Date.now() - 1200),
    },
    {
      id: "2",
      sender: "user",
      content: actualTest.input,
      timestamp: new Date(Date.now() - 1100),
    },
    {
      id: "3",
      sender: "agent",
      content: actualTest.received,
      timestamp: new Date(Date.now() - 1000),
      metadata: {
        responseTime: 180,
        tokensUsed: 156,
        confidence: 0.92,
        model: "gpt-4",
      },
    },
    {
      id: "4",
      sender: "system",
      content: actualTest.success
        ? "✅ Test PASSED - Response matches expected output"
        : "❌ Test FAILED - Response does not match expected output",
      timestamp: new Date(Date.now() - 900),
    },
  ]);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const closeSidebar = () => {
    setIsSidebarOpen(false);
  };

  const getMessageIcon = (sender: string) => {
    switch (sender) {
      case "user":
        return <UserIcon className="h-4 w-4 text-blue-400" />;
      case "agent":
        return <Bot className="h-4 w-4 text-green-400" />;
      case "system":
        return <AlertCircle className="h-4 w-4 text-yellow-400" />;
      default:
        return <MessageSquare className="h-4 w-4 text-gray-400" />;
    }
  };

  const getMessageStyle = (sender: string) => {
    switch (sender) {
      case "user":
        return "bg-blue-900/30 border-blue-600/30 text-blue-100";
      case "agent":
        return "bg-green-900/30 border-green-600/30 text-green-100";
      case "system":
        return "bg-yellow-900/30 border-yellow-600/30 text-yellow-100";
      default:
        return "bg-gray-700 border-gray-600 text-gray-100";
    }
  };

  return (
    <div className="flex h-screen bg-gray-900">
      {/* Mobile Sidebar Overlay */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={closeSidebar}
        />
      )}

      {/* Sidebar */}
      <div
        className={cn(
          "fixed lg:relative z-50 h-full transition-transform duration-300 ease-in-out",
          isSidebarOpen
            ? "translate-x-0"
            : "-translate-x-full lg:translate-x-0",
        )}
      >
        <Sidebar />
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col h-full min-h-0 overflow-hidden">
        {/* Top Header */}
        <header className="bg-gray-800 border-b border-gray-700 px-4 lg:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {/* Mobile Menu Button */}
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden text-gray-300 hover:bg-gray-700"
                onClick={toggleSidebar}
              >
                <Menu className="h-5 w-5" />
              </Button>

              <Button
                variant="ghost"
                size="icon"
                onClick={() => window.history.back()}
                className="text-gray-400 hover:text-gray-300"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-xl lg:text-2xl font-bold text-gray-100">
                  Test: {actualTest.id}
                </h1>
                <p className="text-sm text-gray-400">
                  Test Run #{testRunId} • {actualTest.input.substring(0, 50)}...
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <Button
                variant="outline"
                size="icon"
                className="border-gray-600 text-gray-300 hover:bg-gray-700"
              >
                <Download className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="border-gray-600 text-gray-300 hover:bg-gray-700"
              >
                <Share2 className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="border-gray-600 text-gray-300 hover:bg-gray-700"
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </header>

        {/* Main Content Area */}
        <main className="flex-1 h-full flex flex-col min-h-0 bg-gray-900 overflow-auto">
          <div className="p-4 lg:p-6 space-y-6">
            {/* Conversation */}
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-gray-100 flex items-center space-x-2">
                  <MessageSquare className="h-5 w-5" />
                  <span>Test Conversation</span>
                </CardTitle>
                <CardDescription className="text-gray-400">
                  Step-by-step interaction between the test platform and agent
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {messages.map((message) => (
                    <div
                      key={message.id}
                      className={`flex items-start space-x-3 ${
                        message.sender === "user"
                          ? "justify-end"
                          : "justify-start"
                      }`}
                    >
                      {message.sender !== "user" && (
                        <div className="flex-shrink-0 mt-1">
                          {getMessageIcon(message.sender)}
                        </div>
                      )}

                      <div
                        className={`max-w-[70%] p-3 rounded-lg border ${
                          message.sender === "user"
                            ? "bg-blue-900/30 border-blue-600/30 text-blue-100"
                            : getMessageStyle(message.sender)
                        }`}
                      >
                        <div className="text-sm">{message.content}</div>
                        {message.metadata && (
                          <div className="mt-2 pt-2 border-t border-gray-600/30 text-xs text-gray-400">
                            <div className="grid grid-cols-2 gap-2">
                              {message.metadata.responseTime && (
                                <span>
                                  Response: {message.metadata.responseTime}ms
                                </span>
                              )}
                              {message.metadata.tokensUsed && (
                                <span>
                                  Tokens: {message.metadata.tokensUsed}
                                </span>
                              )}
                              {message.metadata.confidence && (
                                <span>
                                  Confidence:{" "}
                                  {(message.metadata.confidence * 100).toFixed(
                                    1,
                                  )}
                                  %
                                </span>
                              )}
                              {message.metadata.model && (
                                <span>Model: {message.metadata.model}</span>
                              )}
                            </div>
                          </div>
                        )}
                      </div>

                      {message.sender === "user" && (
                        <div className="flex-shrink-0 mt-1">
                          {getMessageIcon(message.sender)}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Expected vs Actual */}
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-gray-100">
                  Expected vs Actual Output
                </CardTitle>
                <CardDescription className="text-gray-400">
                  Comparison of expected and actual responses
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-medium text-gray-100 mb-2">
                      Expected Output:
                    </h4>
                    <div className="p-3 bg-green-900/30 border border-green-600/30 rounded text-green-100 text-sm">
                      {actualTest.expected}
                    </div>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-100 mb-2">
                      Actual Output:
                    </h4>
                    <div
                      className={`p-3 rounded text-sm ${
                        actualTest.success
                          ? "bg-green-900/30 border border-green-600/30 text-green-100"
                          : "bg-red-900/30 border border-red-600/30 text-red-100"
                      }`}
                    >
                      {actualTest.received}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </main>
      </div>
    </div>
  );
};

export default TestDetailsPage;
